import { REGIONS } from 'v2/configs/constants'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { ComplianceApplicationStatus } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { useComplianceGateQuery } from '../number-compliance-v2/queries-v2'
import type { RV2ComplianceApplication } from '../number-compliance-v2/types-v2'
import { describeBusinessKyc } from './business-kyc-status'
import { type IndiaKycStateOptions, isBusinessKycOpen, useIndiaKycStateQuery } from './queries'

/** India KYC v2 (individual or business) is India only, for the orgs its PostHog flag targets. */
export const useIsIndiaIndividualKycEnabled = (): boolean => {
  const { isIndiaIndividualKycEnabled } = useFeatureFlags()
  return getLocalAuth().region === REGIONS.IN && isIndiaIndividualKycEnabled
}

/** The flag, and the KYC state it gates. `enabled` false reads the cache alone. */
export const useIndiaKycV2State = (enabled = true, options?: IndiaKycStateOptions) => {
  const isEnabled = useIsIndiaIndividualKycEnabled()
  return [isEnabled, useIndiaKycStateQuery(isEnabled && enabled, options)] as const
}

// Business KYC copy promises the signatory's Aadhaar eSign only while core's switch is on.
// `enabled` false reads the cache alone, for a host where the state must not be read.
export const useIsBusinessEsignRequired = (enabled = true): boolean => {
  const [, { data }] = useIndiaKycV2State(enabled)
  return data?.business_esign_required === true
}

// Only core's "individual" means the fixed limits. Anything else, unread included, keeps the
// business controls: core enforces the limits anyway.
export const useIsIndividualKycAccount = (): boolean => {
  const [isEnabled, { data }] = useIndiaKycV2State()
  return isEnabled && data?.caf_type === 'individual'
}

const BUSINESS_IN_FLIGHT = new Set([
  ComplianceApplicationStatus.SUBMITTED,
  ComplianceApplicationStatus.REJECTED,
])

const SIGNATORY_FLOW_ALIAS = 'india-business-kyc-'

/** One core filed for a signatory: the KYC state's business block has its status. */
export const isSignatoryFlowApplication = (application: RV2ComplianceApplication) =>
  !!application.alias?.startsWith(SIGNATORY_FLOW_ALIAS)

// Today's business application, in review or awaiting its re-upload, so a second must not start.
// Not one core filed for a signatory: that flow has its own status, and starts again from its form.
export const isBusinessKycInFlight = (application?: RV2ComplianceApplication) =>
  !!application &&
  BUSINESS_IN_FLIGHT.has(application.status) &&
  !isSignatoryFlowApplication(application)

// Unknown is not "none filed": no business KYC is offered until the read shows none in flight,
// neither today's application nor a signatory's open one. Disabled, it reads nothing and offers
// nothing.
export const useBusinessKycAvailability = (enabled = true) => {
  const { data: gate } = useComplianceGateQuery(enabled)
  const [, { data: kyc }] = useIndiaKycV2State(enabled)
  const isOpen = enabled && isBusinessKycOpen(kyc?.business)
  return {
    isRead: !enabled || (!!gate && kyc !== undefined),
    canStart: enabled && !!gate && !isBusinessKycInFlight(gate.applications[0]) && !isOpen,
    isOpen,
    /** The last one stopped (its row reads Not completed), so the next one is its restart. */
    isStopped: enabled && describeBusinessKyc(kyc?.business).isStopped,
  }
}
